import { type FC, useCallback, useMemo, useState } from "react";
import GridGappedContainer, {
  GridGappedContainerSize,
} from "../../../../common/components/GridGappedContainer/GridGappedContainer";
import { IconType } from "../../../../common/components/icon/IconType";
import OrderTable from "./OrderTable";
import OrderTotals from "../../../../Cart/components/CartPage/OrderTotals";
import OrderDetailsAddToCartFlow from "../AddToCart/OrderDetailsAddToCartFlow";
import OrderDetailsAddToWishlistFlow from "../AddToWishlist/OrderDetailsAddToWishlistFlow";
import { Permission, userHasPermission } from "../../../../utils/permissions";
import { useBooleanState } from "../../../../common/hooks/useBooleanState";
import { IncludesSurchargeDisclaimer } from "../../../../common/components/Messages/IncludesSurchargeDisclaimer";
import { getOrderLineItemSurchargeValue } from "../../../common/utils/OrderLineItemUtils";
import { sum } from "../../../../utils/PriceUtils";
import { type DetailedOrderData } from "../../../common/modelsV2";
import { useTranslation } from "react-i18next";
import { type CaseDataObject } from "../ServiceCases/types";
import { strictIsNonNull } from "~/utils/reducers";
import { Button } from "~/common/components/Button/Button";
import { DefaultButtonIcon } from "~/common/components/icon/Icon";

interface OrderDetailsProps {
  data: DetailedOrderData;
  serviceCases?: Record<string, CaseDataObject[]>;
}

const OrderDetails: FC<OrderDetailsProps> = ({ data, serviceCases }) => {
  const { t } = useTranslation();

  const canAddToCart = userHasPermission(Permission.ADD_TO_CART);
  const canAddToWishlist = userHasPermission(Permission.ADD_TO_WISHLIST);
  const showCheckboxes = canAddToCart || canAddToWishlist;
  const [selected, setSelected] = useState<number[]>([]);
  const selectedItems = selected.map((index) => data.products[index]);

  const { val: showAddToCart, setTrue: openAddToCart, setFalse: closeAddToCart } = useBooleanState(false);
  const { val: showAddtoWishlist, setTrue: openAddToWishlist, setFalse: closeAddToWishlist } = useBooleanState(false);

  const selectItem = useCallback((itemIndex: number) => {
    setSelected((selected) => {
      if (selected.indexOf(itemIndex) === -1) {
        return selected.concat(itemIndex);
      } else {
        return selected.filter((item) => item !== itemIndex);
      }
    });
  }, []);
  const selectAll = useCallback(
    (newValue: boolean) => {
      if (newValue) {
        setSelected(
          data ? data.products.map((_, index) => (_.product.url ? index : null)).filter(strictIsNonNull) : [],
        );
      } else {
        setSelected([]);
      }
    },
    [data],
  );

  const totalSurcharge = useMemo(() => {
    return sum(...data.products.map((lineItem) => getOrderLineItemSurchargeValue(lineItem)));
  }, [data]);

  const totalProducts = useMemo(() => {
    let sum = 0;
    data.products.map((product) => {
      if (product.subEntries && product.subEntries?.length > 0) {
        sum += product.subEntries?.length ?? 0;
      } else {
        sum += 1;
      }
    });
    return sum;
  }, [data]);

  const constainsSurcharge = totalSurcharge.value > 0;

  return (
    <>
      <div className="d-flex flex-justify-between item-delivery-title-holder mt-30">
        <p className="m-0 headline-small">{t("orderDetailsPage.header.toBeOrdered", { count: totalProducts })}</p>

        <GridGappedContainer className={"scha__screen-only"} gapSize={GridGappedContainerSize.S_16}>
          {canAddToWishlist && (
            <Button
              disabled={selected.length === 0}
              color={canAddToCart ? "secondary" : "primary"}
              startIcon={<DefaultButtonIcon type={IconType.Bookmark} />}
              onClick={openAddToWishlist}
              data-test-id={"add-checked-to-wishlist"}
            >
              {t("page.orderDetails.addToWishlist.button")}
            </Button>
          )}

          {canAddToCart && (
            <Button
              disabled={window.SCHA.config.disableAllWS || selected.length === 0}
              startIcon={<DefaultButtonIcon type={IconType.Cart} />}
              onClick={openAddToCart}
              data-test-id={"add-checked-to-cart"}
            >
              {t("orderDetailsPage.reorderButton")}
            </Button>
          )}
        </GridGappedContainer>
      </div>

      <OrderTable
        className={"mb-4"}
        selected={selected}
        selectItem={selectItem}
        selectAll={selectAll}
        selectable={showCheckboxes}
        orderDetailsData={data}
        serviceCaseData={serviceCases}
      />

      <div className="scha__screen-only scha__order-details-display__totals--wrapper">
        <div className="scha__order-details-display__totals">
          <OrderTotals
            showPriceOnRequest={false}
            weight={data.totalWeight}
            totalValue={data.totalPrice}
            surcharge={totalSurcharge.value > 0 ? totalSurcharge : undefined}
          />
        </div>
      </div>
      {<IncludesSurchargeDisclaimer hasSurcharge={constainsSurcharge} className={"mt-30"} />}

      {showAddToCart && <OrderDetailsAddToCartFlow onClose={closeAddToCart} selectedOrderItems={selectedItems} />}
      {showAddtoWishlist && (
        <OrderDetailsAddToWishlistFlow onClose={closeAddToWishlist} selectedOrderItems={selectedItems} />
      )}
    </>
  );
};

export default OrderDetails;
